import { Head, Link, usePoll } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";
import { FiArrowRight, FiLock } from "react-icons/fi";
import BottomNav from "../components/BottomNav";
import { Sweep } from "../components/PageHeader";
import logo from "../assets/haio-logo.png";
import { Pass } from "../types";
import {
  lockedMedals,
  surpriseLocked,
  surpriseUnlocked,
  trophy,
  unlockedMedals,
} from "./homeIcons";

// Renders trusted, static design SVG markup (see homeIcons.ts).
const Svg = ({ markup, className }: { markup: string; className: string }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: markup }} />
);

// Per-station colours for the unlocked card (design: Playful Home). Admin-added stations fall back to gold.
const unlockedStyles: Record<
  string,
  { card: string; badge: string; desc: string }
> = {
  shom_app: {
    card: "bg-[#fef8e7]",
    badge: "bg-[#ffc21a] text-[var(--ink)]",
    desc: "text-[#6c5142]",
  },
  jukwon: {
    card: "bg-[#eef8f0]",
    badge: "bg-[#237a37] text-white",
    desc: "text-[var(--ink-soft)]",
  },
  infinence: {
    card: "bg-[#fef0ef]",
    badge: "bg-[var(--hai-o-red)] text-white",
    desc: "text-[var(--ink-soft)]",
  },
};

type Station = {
  key: string;
  name: string;
  tagline: string;
  visitedAt: string | null;
};

type Props = {
  pass: Pass;
  justRegistered: boolean;
  stations: Station[];
  giftClaimedAt: string | null;
};

export default function Home({
  pass,
  justRegistered,
  stations,
  giftClaimedAt,
}: Props) {
  const [sheet, setSheet] = useState(justRegistered);
  // Drawer shown after a crew scan: the new medal, then (once that is closed on the last medal) the finale.
  const [drawer, setDrawer] = useState<Station | "finale" | null>(null);
  const done = stations.filter((s) => s.visitedAt).length;
  const total = stations.length;
  const complete = done === total;

  // Crew scans happen on another device, so poll for new visits. Inertia throttles polling in hidden tabs.
  // ponytail: 5s polling is fine for a few hundred attendees; switch to ActionCable if load becomes a problem.
  const { stop } = usePoll(
    3000,
    { only: ["stations"] },
    { autoStart: !complete },
  );
  useEffect(() => {
    if (complete) stop();
  }, [complete, stop]);
  const seen = useRef(
    new Set(stations.filter((s) => s.visitedAt).map((s) => s.key)),
  );
  useEffect(() => {
    const fresh = stations.find((s) => s.visitedAt && !seen.current.has(s.key));
    if (!fresh) return;
    stations.forEach((s) => s.visitedAt && seen.current.add(s.key));
    setSheet(false);
    setDrawer(fresh);
  }, [stations]);
  const closeDrawer = () =>
    setDrawer(
      drawer !== "finale" && complete && !giftClaimedAt ? "finale" : null,
    );

  const [headline, lead, sticker] = giftClaimedAt
    ? ["Gift claimed!", "Thanks for playing. Enjoy your surprise!", "Claimed!"]
    : complete
      ? [
          "You did it!",
          `All ${total} medals found. Your surprise is waiting.`,
          "Trail complete!",
        ]
      : [
          "Let’s go!",
          `Find all ${total} medals to unlock a surprise.`,
          done ? "Keep going!" : "Let’s start!",
        ];

  return (
    <>
      <Head title="Home" />
      <main className="ho-screen">
        <Sweep />
        <header className="relative px-6 pt-[30px]">
          <img src={logo} alt="Sahajidah Hai-O" className="block w-[90px]" />
          <p className="mt-7 text-base font-bold text-[var(--hai-o-red)]">
            Hey {pass.name}!
          </p>
          <h1 className="text-[40px] leading-[46px] font-extrabold tracking-[-1px]">
            {headline}
          </h1>
          <p className="mt-1 text-[15px] leading-[22px] font-medium">{lead}</p>
        </header>

        <div className="flex flex-col gap-6 px-6 pt-5 pb-10">
          <section
            aria-label="Your progress"
            className="ho-pop relative rounded-[20px] bg-[var(--deep-red)] p-[18px] pb-5 text-white"
          >
            <span className="ho-sticker absolute -top-3.5 right-4 bg-[var(--gold)] text-[var(--ink)]">
              {sticker}
            </span>
            <p className="text-[22px] leading-7 font-extrabold">
              {done} of {total} medals
            </p>
            <p className="mt-0.5 mb-3.5 text-[15px] font-semibold">
              {complete
                ? giftClaimedAt
                  ? "All medals found and gift claimed."
                  : "Every stop done. Nice one!"
                : `${total - done} ${done ? "more " : ""}to go`}
            </p>
            <div className="flex items-center gap-2.5">
              <div
                role="img"
                aria-label={`${done} of ${total} medals collected`}
                className="flex flex-1 gap-1.5"
              >
                {stations.map((s, i) => (
                  <span
                    key={s.key}
                    className={`h-4 flex-1 rounded-full border-2 ${i < done ? "border-[var(--ink)] bg-[var(--gold)]" : "border-white/50 bg-white/30"}`}
                  />
                ))}
              </div>
              <Svg
                markup={trophy}
                className="grid size-11 flex-none place-items-center rounded-full border-2 border-[var(--ink)] bg-[var(--gold)] [&>svg]:size-6"
              />
            </div>
          </section>

          <section aria-label="Your Trail">
            <h2 className="mb-3 text-xl font-extrabold">Your Trail</h2>
            <ol className="flex flex-col gap-3">
              {stations.map((s, i) => {
                const u = unlockedStyles[s.key] ?? unlockedStyles.shom_app;
                return (
                  <li
                    key={s.key}
                    className={`flex min-h-[120px] items-center gap-3 ${i % 2 ? "flex-row-reverse" : ""}`}
                  >
                    {/* Admin-added stations have no bespoke art; reuse the surprise trophy. */}
                    <Svg
                      markup={
                        s.visitedAt
                          ? (unlockedMedals[s.key] ?? surpriseUnlocked)
                          : (lockedMedals[s.key] ?? surpriseLocked)
                      }
                      className="flex-none [&>svg]:h-[100px] [&>svg]:w-[88px]"
                    />
                    <button
                      type="button"
                      onClick={() => setSheet(true)}
                      aria-label={`Show my QR for ${s.name}`}
                      className={`flex min-w-0 flex-1 flex-col gap-1 rounded-2xl px-3.5 py-3 text-left ${
                        s.visitedAt
                          ? `ho-pop ${u.card}`
                          : "border-2 border-dashed border-[var(--line-field)] bg-white"
                      }`}
                    >
                      {s.visitedAt ? (
                        <span className={`ho-sticker ${u.badge}`}>
                          Medal unlocked!
                        </span>
                      ) : (
                        <span className="ho-sticker border-[var(--line-field)] bg-[var(--surface-sunken)] text-[var(--ink-soft)]">
                          <FiLock aria-hidden="true" />
                          Locked
                        </span>
                      )}
                      <h3 className="mt-0.5 text-base font-bold">{s.name}</h3>
                      <p
                        className={`text-[13px] leading-[18px] ${s.visitedAt ? u.desc : "text-[var(--ink-soft)]"}`}
                      >
                        {s.tagline}
                      </p>
                    </button>
                  </li>
                );
              })}
              <li className="flex flex-col items-center gap-1.5 pt-4 text-center">
                <Svg
                  markup={complete ? surpriseUnlocked : surpriseLocked}
                  className={
                    complete ? "[&>svg]:h-20 [&>svg]:w-24" : "[&>svg]:size-16"
                  }
                />
                {giftClaimedAt ? (
                  <>
                    <span className="ho-sticker self-center bg-[linear-gradient(90deg,#F3FFDE_0%,#91FFD0_55%,#B5FAFF_100%)]">
                      Gift redeemed
                    </span>
                    <p className="max-w-[260px] text-sm leading-5 font-semibold">
                      Redeemed on{" "}
                      {new Date(giftClaimedAt).toLocaleString("en-MY", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </p>
                  </>
                ) : complete ? (
                  <>
                    <span className="ho-sticker self-center bg-[linear-gradient(90deg,#fff4d0,#ffc998_50%,#ffb6c3)]">
                      Surprise unlocked!
                    </span>
                    <p className="max-w-[260px] text-sm leading-5 font-semibold">
                      Show your profile QR at the counter to claim it.
                    </p>
                  </>
                ) : (
                  <p className="text-sm font-bold text-[var(--ink-soft)]">
                    Surprise unlocks at {total}/{total}
                  </p>
                )}
              </li>
            </ol>
          </section>

          {complete && !giftClaimedAt && (
            <Link href="/profile" className="ho-btn ho-btn-primary">
              Show my profile QR
              <FiArrowRight aria-hidden="true" />
            </Link>
          )}
        </div>

        <BottomNav current="home" />

        {(sheet || drawer) && (
          <div
            aria-hidden="true"
            className="fixed inset-0 z-[3] bg-[rgba(32,29,28,0.5)]"
            onClick={() => (drawer ? closeDrawer() : setSheet(false))}
          />
        )}

        {drawer && drawer !== "finale" && (
          <div
            className="ho-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Medal unlocked"
          >
            <div className="ho-handle" />
            <Svg
              markup={unlockedMedals[drawer.key] ?? surpriseUnlocked}
              className="mb-4 flex justify-center [&>svg]:h-[130px] [&>svg]:w-[114px]"
            />
            <h2>Medal unlocked!</h2>
            <p className="ho-sheet-sub">
              {drawer.name} · {done} of {total} medals
            </p>
            <button
              type="button"
              className="ho-btn ho-btn-outline"
              onClick={closeDrawer}
              autoFocus
            >
              Close
            </button>
          </div>
        )}

        {drawer === "finale" && (
          <div
            className="ho-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="All medals collected"
          >
            <div className="ho-handle" />
            <Svg
              markup={surpriseUnlocked}
              className="mb-2 flex justify-center [&>svg]:h-20 [&>svg]:w-24"
            />
            <h2>All medals collected!</h2>
            <p className="ho-sheet-sub">
              Show your profile QR at the redemption counter.
            </p>
            <div className="flex flex-col gap-4">
              <Link href="/profile" className="ho-btn ho-btn-primary">
                Show my profile QR
                <FiArrowRight aria-hidden="true" />
              </Link>
              <button
                type="button"
                className="ho-btn ho-btn-outline"
                onClick={() => setDrawer(null)}
                autoFocus
              >
                Close
              </button>
            </div>
          </div>
        )}

        {sheet && (
          <>
            <div
              className="ho-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="My Event Pass"
            >
              <div className="ho-handle" />
              <h2>{justRegistered ? "You’re registered!" : "My Event Pass"}</h2>
              <p className="ho-sheet-sub">
                {justRegistered
                  ? "Show this QR to redeem your welcome gift."
                  : "Use it to identify your pass at each spot"}
              </p>
              <div className="ho-qr">
                <img
                  src={pass.qrImageUrl}
                  alt={`QR code ${pass.qrValue}`}
                  width={200}
                  height={200}
                />
                <span className="text-lg font-bold tracking-widest">
                  {pass.qrValue}
                </span>
                <span className="text-sm text-[var(--ink-soft)]">
                  Ticket {pass.ticket}
                </span>
              </div>
              <button
                type="button"
                className="ho-btn ho-btn-outline"
                onClick={() => setSheet(false)}
                autoFocus
              >
                Close
              </button>
            </div>
          </>
        )}
      </main>
    </>
  );
}
